'use client';

import { Store } from '@/lib/types/customer/restaurant/StoreProfile';
import Link from 'next/link';
import React, { useEffect, useRef, useState } from 'react';
import { storeFront } from '@/lib/utils/routeLinks';
import useSWR from 'swr';
import { getRestaurantsByCityID } from '@/clients/customer/restaurants';

// Swiper imports
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, A11y } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';

interface storeItemType {
  label: Record<string, string> | null;
  link: string | null;
  slug: string | null;
  img_path: string | null;
}

type ShopsCarouselProps = {
  lang: string;
  params?: Record<string, string>;
  sectionData?: any;
};

const fetcher = async (
  cityId: number,
  numberOfShops: number,
  locale?: string
) => {
  return getRestaurantsByCityID(
    cityId,
    {
      per_page: numberOfShops,
      order_by: 'order_index_city',
      order_dir: 'asc',
    },
    {
      ['Accept-Language']: locale || 'en',
    }
  ).then((res) => res?.data);
};

export default function ShopsCarousel({
  lang = 'de',
  sectionData,
  params,
}: ShopsCarouselProps) {
  const cityId = sectionData?.online_shop_city_id;
  const numberOfShops = sectionData?.number_of_shops ?? 40;
  const shouldFetch = !!cityId;

  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  const {
    data: resData,
    error,
    isLoading,
  } = useSWR(
    shouldFetch ? ['getRestaurantsByCityID', cityId, params?.locale] : null,
    ([, cityId, locale]) => fetcher(cityId, numberOfShops, locale),
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  // Ref for Swiper instance
  const swiperRef = useRef<any>(null);

  let storeLists: storeItemType[] = [];

  if (resData?.data?.length) {
    storeLists = resData.data.map((store: Store) => ({
      label:
        typeof store.raw_name === 'object' && store.raw_name !== null
          ? (store.raw_name as unknown as Record<string, string>)
          : null,
      link: null,
      slug: `/${store.slug}`,
      img_path: store.media?.logo?.[0]?.url ?? '',
    }));
  }

  // Calculate slides per view based on screen size
  const getSlidesPerView = () => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth >= 1024) return 10; // Desktop: 10 items
      if (window.innerWidth >= 768) return 6; // Tablet: 6 items
      return 2; // Mobile: 2 items
    }
    return 6; // Default for SSR
  };

  // Calculate slide width based on slides per view
  const getSlideWidth = () => {
    const slidesPerView = getSlidesPerView();
    return `calc((100% - ${(slidesPerView - 1) * 16}px) / ${slidesPerView})`;
  };

  const slidesCount = getSlidesPerView();

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  if (!isLoaded) return <></>;

  // Loading state
  if (isLoading) {
    return (
      <div className="mt-6 flex items-center justify-center gap-4 overflow-hidden">
        {Array.from({ length: slidesCount }, (_, i) => (
          <div
            key={i}
            className="h-20 w-full animate-pulse rounded-lg bg-slate-200"
          ></div>
        ))}
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="mt-12 flex items-center justify-center py-8 text-h5 font-bold text-paragraph">
        {lang === 'de'
          ? 'Fehler beim Laden der Geschäfte'
          : 'Error loading stores'}
      </div>
    );
  }

  // Handle navigation
  const handlePrev = () => {
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.slidePrev();
    }
  };

  const handleNext = () => {
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.slideNext();
    }
  };

  if (storeLists.length <= 0) {
    return null;
  }

  return (
    <div className="mt-6">
      {/* Flex container for alignment */}
      <div className="relative w-full">
        {/* Swiper Container */}
        <Swiper
          ref={swiperRef}
          modules={[Navigation, A11y]}
          slidesPerView={getSlidesPerView()}
          spaceBetween={12}
          className="shops-swiper !px-1"
        >
          {storeLists.map((storeBrand: storeItemType, idx: number) => (
            <SwiperSlide
              key={idx}
              className="flex items-center justify-center py-3"
              style={{ width: getSlideWidth() }}
            >
              <Link
                href={storeFront.restaurants.de() + `${storeBrand.slug}`}
                target="_blank"
                title={storeBrand.label?.[lang]}
                className="flex h-24 w-full items-center justify-center rounded-lg bg-white p-1 shadow-md transition-all duration-200 hover:shadow-lg"
              >
                <div
                  className="h-[92%] w-[92%] bg-contain bg-center bg-no-repeat"
                  style={{ backgroundImage: `url(${storeBrand.img_path})` }}
                ></div>
              </Link>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Large screen buttons */}
        <button
          onClick={handlePrev}
          className="absolute -left-10 top-1/2 hidden -translate-y-1/2 rounded-full border border-gray-300 bg-white p-2 shadow-sm hover:bg-gray-50 lg:flex"
          aria-label="Previous"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M15 19l-7-7 7-7"></path>
          </svg>
        </button>

        <button
          onClick={handleNext}
          className="absolute -right-10 top-1/2 hidden -translate-y-1/2 rounded-full border border-gray-300 bg-white p-2 shadow-sm hover:bg-gray-50 lg:flex"
          aria-label="Next"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M9 5l7 7-7 7"></path>
          </svg>
        </button>

        {/* Mobile / small screen buttons under slides */}
        <div className="mt-4 flex justify-center gap-4 lg:hidden">
          <button
            onClick={handlePrev}
            className="rounded-full border border-gray-300 bg-white p-2 shadow-sm hover:bg-gray-50"
            aria-label="Previous"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M15 19l-7-7 7-7"></path>
            </svg>
          </button>

          <button
            onClick={handleNext}
            className="rounded-full border border-gray-300 bg-white p-2 shadow-sm hover:bg-gray-50"
            aria-label="Next"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 5l7 7-7 7"></path>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
